
import { Skeleton } from "@/components/ui/skeleton";
import { Image } from "@/lib/types";
import ImageCard from "@/components/ImageCard";
import { ImageIcon, CheckCircle } from "lucide-react";

interface ImageGridProps {
  images: Image[];
  loading: boolean;
  onDelete: (id: string) => void;
  selectable?: boolean;
  selectedImages?: string[];
  onToggleSelect?: (id: string) => void;
}

const ImageGrid = ({ 
  images, 
  loading, 
  onDelete, 
  selectable = false, 
  selectedImages = [], 
  onToggleSelect 
}: ImageGridProps) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="relative rounded-lg overflow-hidden">
            <Skeleton className="h-48 w-full" />
            <div className="p-3 flex justify-between">
              <Skeleton className="h-5 w-1/2" />
              <Skeleton className="h-5 w-10" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="text-center py-12 border border-white/10 rounded-lg">
        <div className="mb-3 text-white/60">
          <ImageIcon className="mx-auto h-12 w-12 opacity-50" />
        </div>
        <h3 className="text-lg font-medium">No images yet</h3>
        <p className="text-white/60 mt-2">
          Upload your first image to get started
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {images.map((image) => (
        <div key={image.id} className="relative group">
          <ImageCard image={image} onDelete={onDelete} />
          
          {selectable && (
            <div 
              className={`absolute top-2 left-2 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                selectedImages.includes(image.id)
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-black/50 text-white/70 opacity-0 group-hover:opacity-100'
              }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onToggleSelect) {
                  onToggleSelect(image.id);
                }
              }}
            >
              <CheckCircle className="h-5 w-5" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ImageGrid;
